/**
 * /confirm-email?token=… — confirm a sign-in email change (people QA #5).
 *
 * The link is mailed to the NEW address when someone asks to change their
 * sign-in email from My Profile. Using it needs the SAME person signed in
 * (POST /api/v1/me/email-change/confirm checks the link belongs to the
 * session's login), so a link that reaches a stranger — a typo'd address —
 * can't move anyone's login. Signed out, the page sends them to sign in
 * (with their CURRENT email) and brings them back here. Nothing changes
 * until "Confirm" is pressed, so a mail scanner opening the link is
 * harmless.
 */

import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { z } from "zod";
import { toApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { confirmEmailChange } from "@/lib/api/employee-access";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const confirmSearchSchema = z.object({
  token: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_auth/confirm-email")({
  validateSearch: confirmSearchSchema,
  component: ConfirmEmailPage,
});

function ConfirmEmailPage() {
  const { token } = Route.useSearch();
  const { user, loading, identityOnly, refresh } = useAuth();
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(
    token ? null : "This link is incomplete — open it again from the email, or ask for a new one from My Profile.",
  );
  // Where to come back to after signing in / picking a workspace.
  const here = token ? `/confirm-email?token=${encodeURIComponent(token)}` : "/confirm-email";

  async function onConfirm() {
    if (!token) return;
    setPending(true);
    setError(null);
    try {
      const res = await confirmEmailChange(token);
      setDone(res.email);
      void refresh();
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setPending(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">Confirm Your New Sign-In Email</CardTitle>
        <CardDescription>
          Press confirm to start signing in to WorkRight with this address. Your password
          doesn&apos;t change.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {done ? (
          <Alert data-testid="confirm-email-done">
            <CheckCircle2 aria-hidden className="h-4 w-4" />
            <AlertTitle>Done</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>
                You now sign in with <span className="font-medium">{done}</span> — in every
                company you belong to.
              </p>
              <Button asChild size="sm">
                <Link to="/">Back to WorkRight</Link>
              </Button>
            </AlertDescription>
          </Alert>
        ) : loading ? (
          <div className="flex justify-center py-4">
            <Loader2 aria-hidden className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : !user ? (
          <div className="space-y-3" data-testid="confirm-email-sign-in">
            <p className="text-sm text-muted-foreground">
              To confirm, sign in first with the email you use today — we&apos;ll bring you
              back here.
            </p>
            <Button asChild className="w-full">
              <Link to="/login" search={{ next: here }}>
                Sign In to Confirm
              </Link>
            </Button>
          </div>
        ) : identityOnly ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Choose a workspace first, then we&apos;ll bring you back here.
            </p>
            <Button asChild className="w-full">
              <Link to="/select-workspace" search={{ next: here }}>
                Choose a Workspace
              </Link>
            </Button>
          </div>
        ) : (
          <>
            {error && (
              <Alert variant="destructive" data-testid="confirm-email-error">
                <AlertCircle aria-hidden className="h-4 w-4" />
                <AlertTitle>We couldn&apos;t confirm this change</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <p className="text-sm text-muted-foreground">
              Signed in as <span className="font-medium text-foreground">{user.email}</span>.
            </p>
            <Button
              type="button"
              className="w-full gap-1.5"
              disabled={!token || pending}
              onClick={() => void onConfirm()}
              data-testid="confirm-email-submit"
            >
              {pending && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
              Confirm New Email
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Didn&apos;t ask for this? Close this page — nothing changes.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
